<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';
// == Unichat Extension ==
import {
  getSectors,
  getSectorTeams,
  getAgentAssignments,
  addSectorAgent,
  removeSectorAgent,
} from 'dashboard/unichat/api/unichat.js';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();

// == Unichat Extension: Secretaria → Setor ==
const unichatSectors = ref([]);
const unichatSelectedSectorId = ref('');
const unichatInitialSectorId = ref('');
const unichatSectorTeams = ref([]);
const unichatSelectedTeamId = ref('');
const unichatInitialTeamId = ref('');
const unichatLoadingTeams = ref(false);

const accountId = computed(() => store.getters.getCurrentAccountId);

const loadSectorTeams = async (sectorId, preselectTeamId = null) => {
  unichatSectorTeams.value = [];
  if (!sectorId) return;
  unichatLoadingTeams.value = true;
  try {
    const { data } = await getSectorTeams(accountId.value, sectorId);
    unichatSectorTeams.value = Array.isArray(data) ? data : [];
    if (preselectTeamId) unichatSelectedTeamId.value = preselectTeamId;
  } catch { /* silencioso */ } finally {
    unichatLoadingTeams.value = false;
  }
};

watch(unichatSelectedSectorId, (newId, oldId) => {
  if (newId !== oldId) {
    unichatSelectedTeamId.value = '';
    loadSectorTeams(newId);
  }
});

onMounted(async () => {
  try {
    const { data: sectors } = await getSectors(accountId.value);
    unichatSectors.value = Array.isArray(sectors) ? sectors : [];

    const { data: allAssignments } = await getAgentAssignments(accountId.value);
    const mine = allAssignments?.[props.id];
    if (mine?.sectors?.length) {
      const sectorId = mine.sectors[0].id;
      unichatSelectedSectorId.value = sectorId;
      unichatInitialSectorId.value = sectorId;
    }
    if (mine?.teams?.length) {
      const teamId = mine.teams[0].id;
      unichatInitialTeamId.value = teamId;
      await loadSectorTeams(unichatSelectedSectorId.value, teamId);
    } else if (unichatSelectedSectorId.value) {
      await loadSectorTeams(unichatSelectedSectorId.value);
    }
  } catch { /* silencioso */ }
});

const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
const selectedRoleId = ref(props.customRoleId || props.type);
const agentCredentials = ref({ email: props.email });

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      availability: agentAvailability.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);

    // == Unichat Extension: atualizar lotação ==
    const newSectorId = unichatSelectedSectorId.value;
    const oldSectorId = unichatInitialSectorId.value;
    const newTeamId = unichatSelectedTeamId.value;
    const oldTeamId = unichatInitialTeamId.value;

    if (oldSectorId && oldSectorId !== newSectorId) {
      try { await removeSectorAgent(accountId.value, oldSectorId, props.id); } catch { /* silencioso */ }
    }
    if (newSectorId && newSectorId !== oldSectorId) {
      try { await addSectorAgent(accountId.value, newSectorId, props.id); } catch { /* silencioso */ }
    }
    if (oldTeamId && oldTeamId !== newTeamId) {
      try {
        await window.axios.delete(
          `/api/v1/accounts/${accountId.value}/teams/${oldTeamId}/team_members`,
          { data: { user_ids: [props.id] } }
        );
      } catch { /* silencioso */ }
    }
    if (newTeamId && newTeamId !== oldTeamId) {
      try {
        await store.dispatch('teamMembers/create', {
          teamId: newTeamId,
          agentsList: [props.id],
        });
      } catch { /* silencioso */ }
    }

    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <!-- == Unichat Extension: Secretaria → Setor == -->
      <div v-if="unichatSectors.length" class="w-full">
        <label>
          Secretaria <span class="text-xs text-gray-400">(opcional)</span>
          <select v-model="unichatSelectedSectorId">
            <option value="">Nenhuma</option>
            <option
              v-for="sector in unichatSectors"
              :key="sector.id"
              :value="sector.id"
            >
              {{ sector.name }}
            </option>
          </select>
        </label>
      </div>

      <div v-if="unichatSelectedSectorId" class="w-full">
        <label>
          Setor <span class="text-xs text-gray-400">(opcional)</span>
          <select v-model="unichatSelectedTeamId" :disabled="unichatLoadingTeams">
            <option value="">
              {{ unichatLoadingTeams ? 'Carregando...' : 'Nenhum (apenas na Secretaria)' }}
            </option>
            <option
              v-for="team in unichatSectorTeams"
              :key="team.id"
              :value="team.id"
            >
              {{ team.name }}
            </option>
          </select>
          <span v-if="!unichatLoadingTeams && !unichatSectorTeams.length" class="message">
            Nenhum setor vinculado a esta secretaria.
          </span>
        </label>
      </div>
      <!-- == /Unichat Extension == -->

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            v-if="provider !== 'saml'"
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
